import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { BACKEND_URL } from "@/constants/api";
import { REPORT_FIELD_LABELS } from "@/constants/reportFieldLabels";
import { Button } from "@/components/ui/button";
import { LucideX, LucideCheck } from "lucide-react";
import { FreeTrialReportCardProgressGr16202307 } from "@/reports/progress-gr1-6-2023-07/Page";
import { FreeTrialReportCardProgressGr78202307 } from "@/reports/progress-gr7-8-2023-07/Page";
import { FreeTrialReportCardProvincialGr16202309 } from "@/reports/provincial-gr1-6-2023-09/Page";
import { FreeTrialReportCardProvincialGr78202309 } from "@/reports/provincial-gr7-8-2023-09/Page";
import { FreeTrialReportCardProvincialGr912202202Sem } from "@/reports/provincial-gr9-12-2022-02-sem/Page";

const FORM_BY_TYPE = {
  pgrc1to6_2023_07: FreeTrialReportCardProgressGr16202307,
  pgrc7to8_2023_07: FreeTrialReportCardProgressGr78202307,
  pvrc1to6_2023_09: FreeTrialReportCardProvincialGr16202309,
  pvrc7to8_2023_09: FreeTrialReportCardProvincialGr78202309,
  pvrc9to12_2022_02_sem: FreeTrialReportCardProvincialGr912202202Sem,
};

export const ReportDefaults = () => {
  const { reportGroupId } = useParams();
  const { getToken } = useAuth();

  const [reportType, setReportType] = useState(null);
  const [contextInfo, setContextInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pending values from the form (updated on every change, never auto-saved)
  const [pendingValues, setPendingValues] = useState(null);

  // Confirmation modal state
  const [showConfirm, setShowConfirm] = useState(false);
  const [valuesToSave, setValuesToSave] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    const fetchGroup = async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${BACKEND_URL}api/report-groups/${reportGroupId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Failed to fetch class");
        const json = await res.json();
        setReportType(json.reportType);
        setContextInfo({ grade: json.grade || "", schoolYear: json.schoolYear || "" });
        // Seed pending values with existing defaults so Save works even before any edit
        setPendingValues(json.reportDefaults || {});
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchGroup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportGroupId]);

  // Called by FormComponent on every debounced change — just tracks locally, no network call
  const handleFormChange = useCallback((values) => {
    setPendingValues(values);
  }, []);

  // Called when teacher clicks "Save Defaults" in the form header
  const handleRequestSave = useCallback((values) => {
    const nonEmpty = Object.fromEntries(
      Object.entries(values).filter(([k, v]) => k !== "_completedFields" && String(v ?? "").trim() !== "")
    );
    setValuesToSave(nonEmpty);
    setSaveError("");
    setShowConfirm(true);
  }, []);

  const handleConfirm = async () => {
    setSaving(true);
    setSaveError("");
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/report-groups/${reportGroupId}/report-defaults`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(valuesToSave),
      });
      if (!res.ok) {
        const body = await res.json();
        setSaveError(body.message || "Failed to save defaults.");
        return;
      }
      setShowConfirm(false);
    } catch {
      setSaveError("Failed to save defaults. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="container my-6 text-muted-foreground">Loading...</div>;
  if (error) return <div className="container my-6 text-destructive">Error: {error}</div>;

  const FormComponent = FORM_BY_TYPE[reportType];
  if (!FormComponent) {
    return (
      <div className="container my-6 text-muted-foreground">
        Unknown report type: <code>{reportType}</code>
      </div>
    );
  }

  const fieldLabels = REPORT_FIELD_LABELS[reportType] ?? [];
  // Build a label lookup for displaying field names in the confirmation modal
  const labelByKey = Object.fromEntries(fieldLabels.map((f) => [f.key, f.label]));
  // Group the saved values by step for the confirmation list
  const stepOrder = [...new Set(fieldLabels.map((f) => f.step))];
  const byStep = stepOrder.map((step) => ({
    step,
    fields: fieldLabels
      .filter((f) => f.step === step && valuesToSave?.[f.key] !== undefined)
      .map((f) => ({ key: f.key, label: f.label, value: valuesToSave[f.key] })),
  })).filter((s) => s.fields.length > 0);

  return (
    <>
      <FormComponent
        defaultsGroupId={reportGroupId}
        backUrl={`/classes/${reportGroupId}`}
        contextInfo={contextInfo}
        onFormChange={handleFormChange}
        onRequestSave={handleRequestSave}
      />

      {/* Confirmation modal */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
            onClick={() => { if (!saving) setShowConfirm(false); }}
          />
          <div className="relative bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md flex flex-col max-h-[80vh]">
            {/* Header */}
            <div className="flex items-start justify-between px-6 py-5 border-b border-border shrink-0">
              <div>
                <h2 className="text-base font-semibold text-foreground">Confirm Default Values</h2>
                <p className="text-sm text-muted-foreground mt-1 leading-snug">
                  The following values will be written to all existing reports in this class.
                </p>
              </div>
              {!saving && (
                <button
                  onClick={() => setShowConfirm(false)}
                  className="ml-4 mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors shrink-0"
                >
                  <LucideX size={16} />
                </button>
              )}
            </div>

            {/* Field list */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
              {byStep.length === 0 ? (
                <p className="text-sm text-muted-foreground">No values to save. Fill in at least one field first.</p>
              ) : (
                byStep.map(({ step, fields }) => (
                  <div key={step}>
                    <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2 pb-1 border-b border-border">
                      {step}
                    </p>
                    <ul className="space-y-1.5">
                      {fields.map(({ key, label, value }) => (
                        <li key={key} className="flex items-start gap-2 text-sm">
                          <LucideCheck size={14} className="shrink-0 mt-0.5 text-blue-500" />
                          <span>
                            <span className="font-medium text-foreground">{label}:</span>{" "}
                            <span className="text-muted-foreground">{value}</span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))
              )}
            </div>

            {/* Footer */}
            <div className="px-6 pb-5 pt-4 border-t border-border shrink-0 space-y-3">
              {saveError && <p className="text-sm text-destructive">{saveError}</p>}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowConfirm(false)}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleConfirm}
                  disabled={saving || byStep.length === 0}
                >
                  {saving ? "Saving…" : "Apply to All Reports"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
